import { useUnit } from 'effector-react';
import { Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { $project, ItemIcon, usageCount } from '../../entities/project';
import { itemSelected } from '../../entities/selection';
import { EmptyState } from '../../shared/ui';

/** Items used by more than one variant; editing one changes it everywhere. */
export function SharedItemsPanel() {
  const { t } = useTranslation();
  const [project, select] = useUnit([$project, itemSelected]);
  if (!project) return null;
  const counts = usageCount(project);
  const shared = Object.values(project.items).filter((i) => (counts.get(i.id) ?? 0) > 1);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <header className="px-4 pt-3 pb-2">
        <h2 className="text-base font-medium">{t('shared.title')}</h2>
        <p className="mt-0.5 pr-6 text-xs text-text-3">{t('shared.description')}</p>
      </header>
      {shared.length === 0 ? (
        <EmptyState
          icon={<Link2 />}
          title={t('shared.emptyTitle')}
          body={t('shared.emptyBody')}
          className="py-12"
        />
      ) : (
        <ul className="px-2 pb-3">
          {shared.map((item) => {
            const variants = project.scenarios.filter((s) => s.itemIds.includes(item.id));
            const first = variants[0];
            return (
              <li key={item.id}>
                <button
                  type="button"
                  disabled={!first}
                  onClick={() => {
                    if (first) select({ itemId: item.id, scenarioId: first.id });
                  }}
                  className="flex w-full flex-col gap-1 rounded-md px-2 py-2 text-left outline-none hover:bg-bg-3 focus-visible:ring-2 focus-visible:ring-focus"
                >
                  <span className="flex items-center gap-2 text-sm">
                    <ItemIcon item={item} className="size-3.5 text-text-3" />
                    {item.name}
                  </span>
                  <span className="flex flex-wrap gap-1 pl-5.5">
                    {variants.map((s) => (
                      <span
                        key={s.id}
                        className="inline-flex items-center gap-1 rounded-sm bg-bg-3 px-1.5 py-0.5 text-xs text-text-2"
                      >
                        <span
                          aria-hidden
                          className="size-1.5 rounded-full"
                          style={{ background: `var(--chart-series-${String(s.color + 1)})` }}
                        />
                        {s.name}
                      </span>
                    ))}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
